import { createApi } from "@reduxjs/toolkit/query/react";

import { dynamicBaseQuery } from "@/api/DynamicBaseQuery";
import type { IApiResponse } from "@/types";
import type {
  InstallAppBody,
  ListAppReviewsQuery,
  ListMyAppsQuery,
  AppStoreListQuery,
  MyApp,
  MyAppReview,
  RunMyAppBody,
  RunMyAppResult,
  SaveMyAppBody,
  UpsertAppReviewBody,
} from "@/types/(company)/my-app.interface";

// RTK Query slice for the "My App" module. Generation (create_stream) is SSE
// and consumed separately via a streaming fetch hook; everything here is plain
// request/response. Responses keep the backend `IApiResponse<T>` envelope; call
// sites read `res.data` themselves.
export const myAppApi = createApi({
  reducerPath: "myAppApi",
  baseQuery: dynamicBaseQuery,
  tagTypes: [
    "MyApps",
    "MyApp",
    "MyAppAppStore",
    "MyAppReviews",
    "MyAppReview",
  ],
  endpoints: (builder) => ({
    // ── Library (paginated) ───────────────────────────────────────────────
    listMyApps: builder.query<IApiResponse<MyApp[]>, ListMyAppsQuery>({
      query: (q) => ({
        url: "/company/my-apps",
        method: "GET",
        params: {
          page: q.page,
          limit: q.limit,
          status: q.status,
          search: q.search || undefined,
          sort: q.sort,
          filter: q.filter,
        },
      }),
      providesTags: ["MyApps"],
    }),

    // ── Detail ────────────────────────────────────────────────────────────
    getMyApp: builder.query<IApiResponse<MyApp>, number>({
      query: (id) => ({ url: `/company/my-apps/${id}`, method: "GET" }),
      providesTags: (_r, _e, id) => [{ type: "MyApp", id }],
    }),

    // ── Save (upsert by company+slug) ─────────────────────────────────────
    saveMyApp: builder.mutation<IApiResponse<MyApp>, SaveMyAppBody>({
      query: (body) => ({ url: "/company/my-apps", method: "POST", body }),
      // Invalidate the specific app too — an upsert may update an existing row
      // (e.g. a whole-app update), so its detail view must refetch.
      invalidatesTags: (res) => [
        "MyApps",
        ...(res?.data?.id ? [{ type: "MyApp" as const, id: res.data.id }] : []),
      ],
    }),

    // ── Draft (auto-saved as soon as a generation finishes) ───────────────
    // Always creates a new row, so a generation can never overwrite an existing
    // app. Confirming the preview is an updateMyApp with status "active".
    saveMyAppDraft: builder.mutation<IApiResponse<MyApp>, SaveMyAppBody>({
      query: (body) => ({
        url: "/company/my-apps/drafts",
        method: "POST",
        body,
      }),
      invalidatesTags: ["MyApps"],
    }),

    // ── Update by id ──────────────────────────────────────────────────────
    updateMyApp: builder.mutation<
      IApiResponse<MyApp>,
      { id: number; body: SaveMyAppBody }
    >({
      query: ({ id, body }) => ({
        url: `/company/my-apps/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_r, _e, { id }) => ["MyApps", { type: "MyApp", id }],
    }),

    // ── Delete ────────────────────────────────────────────────────────────
    deleteMyApp: builder.mutation<IApiResponse<{ deleted: boolean }>, number>({
      query: (id) => ({ url: `/company/my-apps/${id}`, method: "DELETE" }),
      // Deleting an installed copy (or a published original) must clear the
      // store's "Open" state back to "Install" — the app-store list/detail
      // queries cache `installedAppId` and won't know a copy disappeared
      // unless this tag is invalidated too.
      invalidatesTags: ["MyApps", "MyAppAppStore"],
    }),

    // ── Logo/thumbnail ────────────────────────────────────────────────────
    uploadMyAppLogo: builder.mutation<
      IApiResponse<MyApp>,
      { id: number; file: File }
    >({
      query: ({ id, file }) => {
        const formData = new FormData();
        formData.append("logo", file);
        return {
          url: `/company/my-apps/${id}/logo`,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: (_r, _e, { id }) => [
        "MyApps",
        "MyAppAppStore",
        { type: "MyApp", id },
      ],
    }),
    removeMyAppLogo: builder.mutation<IApiResponse<MyApp>, number>({
      query: (id) => ({
        url: `/company/my-apps/${id}/logo`,
        method: "DELETE",
      }),
      invalidatesTags: (_r, _e, id) => [
        "MyApps",
        "MyAppAppStore",
        { type: "MyApp", id },
      ],
    }),

    // ── Run (open / refresh / input submit) ───────────────────────────────
    runMyApp: builder.mutation<IApiResponse<RunMyAppResult>, RunMyAppBody>({
      query: (body) => ({ url: "/company/my-apps/run", method: "POST", body }),
    }),

    // ── Publish / unpublish to the app store ─────────────────────────────
    publishMyApp: builder.mutation<IApiResponse<MyApp>, number>({
      query: (id) => ({
        url: `/company/my-apps/${id}/publish`,
        method: "POST",
      }),
      invalidatesTags: (_r, _e, id) => [
        "MyApps",
        "MyAppAppStore",
        { type: "MyApp", id },
      ],
    }),
    unpublishMyApp: builder.mutation<IApiResponse<MyApp>, number>({
      query: (id) => ({
        url: `/company/my-apps/${id}/unpublish`,
        method: "POST",
      }),
      invalidatesTags: (_r, _e, id) => [
        "MyApps",
        "MyAppAppStore",
        { type: "MyApp", id },
      ],
    }),
    // Cancel a PENDING submission while keeping any live copy in the store.
    withdrawSubmission: builder.mutation<IApiResponse<MyApp>, number>({
      query: (id) => ({
        url: `/company/my-apps/${id}/withdraw-submission`,
        method: "POST",
      }),
      invalidatesTags: (_r, _e, id) => [
        "MyApps",
        "MyAppAppStore",
        { type: "MyApp", id },
      ],
    }),

    // ── App Store (global catalog) ───────────────────────────────────────
    listAppStore: builder.query<IApiResponse<MyApp[]>, AppStoreListQuery>({
      query: (q) => ({
        url: "/company/my-apps/app-store",
        method: "GET",
        params: {
          page: q.page,
          limit: q.limit,
          search: q.search || undefined,
          module: q.module || undefined,
        },
      }),
      providesTags: ["MyAppAppStore"],
    }),

    // The catalog's real categories + counts, for the store's filter row.
    listAppStoreModules: builder.query<
      IApiResponse<Array<{ module: string; count: number }>>,
      void
    >({
      query: () => ({
        url: "/company/my-apps/app-store/modules",
        method: "GET",
      }),
      providesTags: ["MyAppAppStore"],
    }),

    getAppStoreApp: builder.query<IApiResponse<MyApp>, number>({
      query: (id) => ({
        url: `/company/my-apps/app-store/${id}`,
        method: "GET",
      }),
      providesTags: (_r, _e, id) => [{ type: "MyAppAppStore", id }],
    }),

    installAppStoreApp: builder.mutation<
      IApiResponse<MyApp>,
      { id: number; body: InstallAppBody }
    >({
      query: ({ id, body }) => ({
        url: `/company/my-apps/app-store/${id}/install`,
        method: "POST",
        body,
      }),
      // The installed copy lands in the caller's library, and the catalog row's
      // install count + "Installed" state both change — refetch the store too.
      invalidatesTags: ["MyApps", "MyAppAppStore"],
    }),

    // ── Ratings & reviews on a catalog app ────────────────────────────────
    listAppReviews: builder.query<
      IApiResponse<MyAppReview[]>,
      { id: number } & ListAppReviewsQuery
    >({
      query: ({ id, page, limit }) => ({
        url: `/company/my-apps/app-store/${id}/reviews`,
        method: "GET",
        params: { page, limit },
      }),
      providesTags: (_r, _e, { id }) => [{ type: "MyAppReviews", id }],
    }),

    // The caller company's own review, to preload the write/edit form.
    getMyAppReview: builder.query<IApiResponse<MyAppReview | null>, number>({
      query: (id) => ({
        url: `/company/my-apps/app-store/${id}/reviews/mine`,
        method: "GET",
      }),
      providesTags: (_r, _e, id) => [{ type: "MyAppReview", id }],
    }),

    upsertAppReview: builder.mutation<
      IApiResponse<MyAppReview>,
      { id: number; body: UpsertAppReviewBody }
    >({
      query: ({ id, body }) => ({
        url: `/company/my-apps/app-store/${id}/reviews`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "MyAppReviews", id },
        { type: "MyAppReview", id },
        "MyAppAppStore",
      ],
    }),

    deleteAppReview: builder.mutation<
      IApiResponse<{ deleted: boolean }>,
      number
    >({
      query: (id) => ({
        url: `/company/my-apps/app-store/${id}/reviews`,
        method: "DELETE",
      }),
      invalidatesTags: (_r, _e, id) => [
        { type: "MyAppReviews", id },
        { type: "MyAppReview", id },
        "MyAppAppStore",
      ],
    }),
  }),
});

export const {
  useListMyAppsQuery,
  useGetMyAppQuery,
  useSaveMyAppMutation,
  useSaveMyAppDraftMutation,
  useUpdateMyAppMutation,
  useDeleteMyAppMutation,
  useUploadMyAppLogoMutation,
  useRemoveMyAppLogoMutation,
  useRunMyAppMutation,
  usePublishMyAppMutation,
  useUnpublishMyAppMutation,
  useWithdrawSubmissionMutation,
  useListAppStoreQuery,
  useListAppStoreModulesQuery,
  useGetAppStoreAppQuery,
  // Used by the install wizard to re-check the listing is still live before
  // cloning it (an app can be unpublished between browsing and installing).
  useLazyGetAppStoreAppQuery,
  useInstallAppStoreAppMutation,
  useListAppReviewsQuery,
  useGetMyAppReviewQuery,
  useUpsertAppReviewMutation,
  useDeleteAppReviewMutation,
} = myAppApi;
